import { PRAYER_KEYS, } from './types.js';
export const METHOD_SPECS = {
    diyanet: {
        code: 'diyanet',
        aladhanId: 13,
        fajrAngle: 18,
        ishaAngle: 17,
        maghribAngle: null,
        builtIn: { sunrise: -7, dhuhr: 5, asr: 4, maghrib: 7 },
        tradition: 'sunni',
        nightRule: 'angle_based',
    },
    mwl: {
        code: 'mwl',
        aladhanId: 3,
        fajrAngle: 18,
        ishaAngle: 17,
        maghribAngle: null,
        builtIn: {},
        tradition: 'sunni',
        nightRule: 'angle_based',
    },
    uoif: {
        code: 'uoif',
        aladhanId: 12,
        fajrAngle: 12,
        ishaAngle: 12,
        maghribAngle: null,
        builtIn: {},
        tradition: 'sunni',
        nightRule: 'angle_based',
    },
    morocco: {
        code: 'morocco',
        aladhanId: 21,
        fajrAngle: 19,
        ishaAngle: 17,
        maghribAngle: null,
        builtIn: { dhuhr: 5, maghrib: 5 },
        tradition: 'sunni',
        nightRule: 'angle_based',
    },
    jafari: {
        code: 'jafari',
        aladhanId: 0,
        fajrAngle: 16,
        ishaAngle: 14,
        maghribAngle: 4,
        builtIn: {},
        tradition: 'shia',
        nightRule: 'angle_based',
    },
    // Europese gezamenlijke kalender van Diyanet (ISN/DITIB) en Milli Görüş (IGMG), sinds 1 januari
    // 2023 (docs/onderzoek/feedback-26-sep.md §2): Fajr 18°, Isha 16°, en bij "echte moeite" in de
    // korte zomernachten een regel met een derde van de nacht. De officiële regeltekst voor de zomer
    // is niet openbaar; nightRule 'eu_joint' (calc.ts) is gefit op de volledige gepubliceerde
    // Diyanet-jaartabellen van Rotterdam, Amsterdam en Hamburg: het hele jaar binnen 2 minuten
    // (Hamburg 3), nooit aan de onveilige kant. De tabellen van Groningen en Maastricht wijken 's zomers
    // lokaal af (tot 9 en 5 minuten, wel aan de veilige kant). Daarom zegt de UI "stem af op je
    // moskee". AlAdhan kent deze methode niet (aladhanId -1).
    eu_joint: {
        code: 'eu_joint',
        aladhanId: -1,
        fajrAngle: 18,
        ishaAngle: 16,
        maghribAngle: null,
        builtIn: { sunrise: -7, dhuhr: 5, asr: 4, maghrib: 7 },
        tradition: 'sunni',
        nightRule: 'eu_joint',
    },
};
/** Europa (ISO 3166-1): hier volgen Turkse moskeeën de gezamenlijke kalender, niet die van Turkije. */
export const EUROPE_COUNTRY_CODES = [
    'AD',
    'AL',
    'AT',
    'BA',
    'BE',
    'BG',
    'CH',
    'CY',
    'CZ',
    'DE',
    'DK',
    'EE',
    'ES',
    'FI',
    'FR',
    'GB',
    'GR',
    'HR',
    'HU',
    'IE',
    'IS',
    'IT',
    'LI',
    'LT',
    'LU',
    'LV',
    'MC',
    'MD',
    'ME',
    'MK',
    'MT',
    'NL',
    'NO',
    'PL',
    'PT',
    'RO',
    'RS',
    'SE',
    'SI',
    'SK',
    'SM',
    'UA',
    'XK',
];
/**
 * Landen waar de gezamenlijke kalender (eu_joint) de standaard is en waar 0041 Diyanet-gezinnen
 * naartoe zette (fixer golf 6b r2, G6B-FS-R2-3; integratie golf 6b live r1). De rekenregels zijn
 * gemeten op Rotterdam, Amsterdam en Hamburg (51,9-53,6° NB); verder naar het noorden (Stockholm,
 * 59,3°) liggen imsak en yatsı 's zomers tot 2-3 minuten aan de onveilige kant (106 dagen). Daarom:
 * alleen in deze landen EN niet boven EU_JOINT_MAX_LAT (Denemarken boven Midden-Jutland, Skagen,
 * Aalborg: Diyanet). Zweden, Noorwegen, Finland en IJsland nooit. Daar blijft Diyanet (Turkije) de
 * standaard; eu_joint kan wel met de hand gekozen worden. Zelfde regel als in de SQL van 0041.
 */
export const EU_JOINT_COUNTRY_CODES = [
    'AT',
    'BE',
    'CH',
    'DE',
    'DK',
    'FR',
    'LU',
    'NL',
];
/** Noordgrens (graden NB) van de gezamenlijke kalender als standaard: ongeveer 56° (Hamburg 53,6°). */
export const EU_JOINT_MAX_LAT = 56;
/**
 * Past de gezamenlijke kalender hier? Land in EU_JOINT_COUNTRY_CODES en breedte hooguit
 * EU_JOINT_MAX_LAT. Land onbekend = ja (de dienst draait nu alleen in Nederland); breedte onbekend =
 * alleen het land telt.
 */
export function euJointFits(countryCode, lat = null) {
    if (lat !== null && lat !== undefined && lat > EU_JOINT_MAX_LAT)
        return false;
    if (!countryCode)
        return true;
    return EU_JOINT_COUNTRY_CODES.indexOf(countryCode.toUpperCase()) >= 0;
}
/** Ligt dit land in Europa (onbekend = ja: de dienst draait nu alleen in Nederland)? */
export function isEuropeanCountry(countryCode) {
    if (!countryCode)
        return true;
    return EUROPE_COUNTRY_CODES.indexOf(countryCode.toUpperCase()) >= 0;
}
/**
 * Standaardmethode bij het instellen (golf 6c: volgt de mazhab, niet meer de keuken). Hanafi: in
 * Europa de gezamenlijke kalender (Diyanet · Milli Görüş), daarbuiten Diyanet (Turkije). Een andere
 * school of "weet ik niet": MWL. Altijd bij naam getoond en met één tik te wijzigen. Dezelfde regel
 * als methodForMadhab in madhab.ts. Europa betekent hier: waar de gezamenlijke kalender gemeten is
 * (euJointFits: land en breedte ≤ 56°; integratie golf 6b live r1), elders Diyanet.
 */
export function defaultMethod(madhab, countryCode = null, lat = null) {
    if (madhab !== 'hanafi')
        return 'mwl';
    return euJointFits(countryCode, lat) ? 'eu_joint' : 'diyanet';
}
/**
 * Wordt een huishouden met deze methode en dit land overgezet op de gezamenlijke kalender
 * (0041_gebed_bibliotheek doet hetzelfde in SQL, met een eenmalige melding)?
 */
export function movesToEuJoint(method, countryCode, lat = null) {
    return method === 'diyanet' && euJointFits(countryCode, lat);
}
/**
 * Weken met de zomerregel van de gezamenlijke kalender (half april tot half september, Europa): dan
 * kan een moskee elders een paar minuten van onze berekening afwijken, dus staat "Stem af op je
 * moskee" bij de tijden op Vandaag en op de tablet (review golf 6b, G6B-FS-R1-2).
 */
export function inSummerRuleWeeks(method, date) {
    if (method !== 'eu_joint')
        return false;
    const md = date.slice(5);
    return md >= '04-15' && md <= '09-15';
}
/**
 * Standaard voor de namen (İmsak/Öğle/İkindi of Fajr/Dhuhr/Asr). Golf 6c (review ronde 1): een
 * taalkeuze, dus nooit meer uit de keuken. Turks bij de kalender van Diyanet of de gezamenlijke
 * kalender van Diyanet en Milli Görüş (die kalenders gebruiken zelf de Turkse namen), of als het
 * gezin de app in het Turks gebruikt; anders Arabisch. Alleen voor een gezin zonder opgeslagen
 * keuze: wat het gezin zelf koos (de rij in prayer_settings) blijft altijd staan.
 */
export function defaultNaming(method, locale) {
    if (method === 'eu_joint' || method === 'diyanet')
        return 'turkish';
    return typeof locale === 'string' && /^tr($|[-_])/i.test(locale) ? 'turkish' : 'arabic';
}
/**
 * Grenzen per tijd. Fajr en Isha ruim (± 90): daar verschillen methoden en moskeeën in de zomer
 * het meest. De rest ± 30 (moskeeën ronden af of wachten een paar minuten).
 */
export const OFFSET_LIMITS = Object.freeze({
    fajr: 90,
    sunrise: 30,
    dhuhr: 30,
    asr: 30,
    maghrib: 30,
    isha: 90,
});
/** Voor de Zod-schema's: de ruimste grens. */
export const OFFSET_ABS_MAX = 90;
export const ZERO_OFFSETS = Object.freeze({
    fajr: 0,
    sunrise: 0,
    dhuhr: 0,
    asr: 0,
    maghrib: 0,
    isha: 0,
});
export const EMPTY_OFFSET_PLAN = Object.freeze({
    base: ZERO_OFFSETS,
    months: {},
});
/**
 * Voorgestelde afwijkingen. Diyanet in Nederland: de officiële tabel Rotterdam (stad 13980) wijkt
 * 24 sep-1 okt 2026 consequent af van de methode (AlAdhan 13, dus ook calc.ts): Isha −5,
 * Maghrib +2 (rest binnen een minuut). Zomer en winter zijn NIET vergeleken; daarom alleen als
 * basis voorgesteld, met het label "alleen gecontroleerd voor september".
 */
export function seedOffsets(method, countryCode) {
    if (method === 'diyanet' && countryCode === 'NL') {
        return {
            plan: { base: { ...ZERO_OFFSETS, maghrib: 2, isha: -5 }, months: {} },
            seed: 'diyanet_nl_2026_09',
        };
    }
    return { plan: { base: { ...ZERO_OFFSETS }, months: {} }, seed: null };
}
/** Opgeslagen afwijkingen → volledig en geklemd per tijd (onbekend of ongeldig = 0). */
export function normalizeOffsets(value) {
    const out = { ...ZERO_OFFSETS };
    if (!value)
        return out;
    for (const key of PRAYER_KEYS) {
        const v = value[key];
        if (typeof v === 'number' && isFinite(v)) {
            const lim = OFFSET_LIMITS[key];
            out[key] = Math.max(-lim, Math.min(lim, Math.round(v)));
        }
    }
    return out;
}
const MONTH_KEYS = [
    '1',
    '2',
    '3',
    '4',
    '5',
    '6',
    '7',
    '8',
    '9',
    '10',
    '11',
    '12',
];
/** Opgeslagen plan → genormaliseerd (onbekende maanden vallen weg). */
export function normalizeOffsetPlan(value) {
    if (!value)
        return { base: { ...ZERO_OFFSETS }, months: {} };
    const base = normalizeOffsets((value.base ?? null));
    const months = {};
    const raw = value.months;
    if (raw && typeof raw === 'object') {
        for (const k of MONTH_KEYS) {
            const m = raw[k];
            if (m && typeof m === 'object') {
                months[k] = normalizeOffsets(m);
            }
        }
    }
    return { base, months };
}
/** Welke afwijkingen gelden op deze dag (maandset als die er is, anders de basis)? */
export function offsetsFor(plan, date) {
    const month = String(Number(date.slice(5, 7)));
    return plan.months[month] ?? plan.base;
}
//# sourceMappingURL=methods.js.map